import { ReactNode, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  ButtonProps,
} from '@mui/material'

import { CloseIcon, OkIcon } from 'shared/icons'

import { Loading } from 'components/controls/loading'

type ConfirmDialogProps = {
  isOpen?: boolean
  isLoading?: boolean
  title?: string
  message: string | ReactNode
  backdropClose?: boolean
  ok?: {
    label?: string
    icon?: React.ReactNode
    variant?: ButtonProps["variant"]
    color?: ButtonProps["color"]
    onClick: () => void
  }
  onOk?: () => void
  cancel?: {
    label?: string
    icon?: React.ReactNode
    variant?: ButtonProps["variant"]
    color?: ButtonProps["color"]
    onClick: () => void
  }
  onCancel?: () => void
  onClose?: () => void
}
export const ConfirmDialog = ({
  isOpen = true,
  isLoading,
  title,
  message,
  backdropClose,
  ok,
  onOk,
  cancel,
  onCancel,
  onClose
}: ConfirmDialogProps) => {
  const onDialogClose = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any, reason: "backdropClick" | "escapeKeyDown") => {
      if (backdropClose !== true && reason === 'backdropClick') return
      if (onCancel) onCancel()
      else if (cancel?.onClick) cancel.onClick()
      else if (onClose) onClose()
    },
    [backdropClose, onCancel, cancel, onClose]
  )
  const onDialogSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (onOk) onOk()
      else if (ok?.onClick) ok.onClick()
    },
    [onOk, ok]
  )
  return isLoading ? (
    <Loading />
  ) : (
    <Dialog
      open={isOpen ?? true}
      fullWidth
      maxWidth="sm"
      onClose={onDialogClose}
    >
      <form onSubmit={onDialogSubmit}>
        {title && (
          <DialogTitle>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Typography
                variant="h5"
                color="text.primary"
                flexGrow={1}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {title}
              </Typography>
              <Tooltip title="閉じる">
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </DialogTitle>
        )}
        <DialogContent>
          {typeof message === 'string' ? (
            <Typography variant="h5" padding={2}>{message}</Typography>
          ) : message}

          <DialogActions sx={{ marginTop: 2 }}>
            { ok ? (
              <Button
                color={ok.color}
                variant={ok.variant ?? "contained"}
                size="large"
                startIcon={ok.icon ? ok.icon : <OkIcon/>}
                type="submit"
              >
                {ok.label ?? "OK"}
              </Button>
            ) : onOk ? (
              <Button variant="contained" size="large" startIcon={<OkIcon/>} type="submit">
                OK
              </Button>
            ) : <></>}

            { cancel ? (
              <Button
                color={cancel.color}
                variant={cancel.variant ?? "text"}
                size="large"
                startIcon={cancel.icon}
                onClick={cancel.onClick}
              >
                {cancel.label ?? "キャンセル"}
              </Button>
            ) : onCancel ? (
              <Button variant="text" size="large" onClick={onCancel}>
                キャンセル
              </Button>
            ) : <></>}
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  )
}
